import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { FormMessageHOC } from 'common';
import { BasicModal, DefaultButton } from 'common/components';
import { CheckBox } from 'crm/_components';

import { getPartnershipLinkDistributions } from '../../api';

import { staticStyles } from './style';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const SelectLinkDistributionModal = ({
  isOpen,
  userId,
  onRequestClose,
  onConfirm,
  showFormSuccessMessage,
  showFormFailMessage,
}) => {
  const [items, setItems] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [tags, setTags] = useState([]);
  const [draftTypeFilter, setDraftTypeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadedRequestKey, setLoadedRequestKey] = useState('');
  const requestKey = `${isOpen ? 'open' : 'closed'}:${userId}:${page}:${pageSize}:${typeFilter}:${tagFilter}`;

  useEffect(() => {
    if (isOpen) {
      setSelectedCodes([]);
      setPage(1);
      setLoadedRequestKey('');
    }
  }, [isOpen, userId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let mounted = true;
    setIsLoading(true);
    const params = {
      page,
      page_size: pageSize,
      user_id: userId,
      ...(typeFilter ? { search: typeFilter } : {}),
      ...(tagFilter ? { tag: tagFilter } : {}),
    };

    setItems([]);
    getPartnershipLinkDistributions(params)
      .then(({ data }) => {
        if (!mounted) return;
        const payload = data || {};
        let results = [];
        if (Array.isArray(payload.results)) {
          results = payload.results;
        } else if (Array.isArray(payload)) {
          results = payload;
        }
        setItems(results);
        setTags(Array.isArray(payload.tags) ? payload.tags : []);
        setTotal(payload.count ?? results.length);
        setLoadedRequestKey(requestKey);
      })
      .catch(() => {
        if (mounted) {
          setItems([]);
          setTags([]);
          setTotal(0);
          setLoadedRequestKey(requestKey);
          showFormFailMessage('formFailMessageText');
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isOpen, page, pageSize, requestKey, showFormFailMessage, tagFilter, typeFilter, userId]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const visibleItems = loadedRequestKey === requestKey && !isLoading ? items : [];
  const selectableVisibleItems = visibleItems.filter(item => !item.isUnavailable);
  const allVisibleSelected =
    selectableVisibleItems.length > 0 && selectableVisibleItems.every(item => selectedCodes.includes(item.code));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const toggleSelected = item => {
    if (item.isUnavailable) return;
    const { code } = item;
    setSelectedCodes(current =>
      current.includes(code) ? current.filter(itemCode => itemCode !== code) : [...current, code]
    );
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedCodes(current => current.filter(code => !selectableVisibleItems.some(item => item.code === code)));
    } else {
      setSelectedCodes(current => Array.from(new Set([...current, ...selectableVisibleItems.map(item => item.code)])));
    }
  };

  const applySearch = () => {
    setTypeFilter(draftTypeFilter.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setDraftTypeFilter('');
    setTypeFilter('');
    setTagFilter('');
    setPage(1);
  };

  const close = () => {
    if (!isSubmitting) onRequestClose();
  };

  const confirm = async () => {
    if (!selectedCodes.length || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onConfirm(selectedCodes);
      showFormSuccessMessage('partnershipLinkDistributionSuccess');
      onRequestClose();
    } catch (error) {
      showFormFailMessage(error.response?.data?.detail || 'formFailMessageText');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicModal
      isOpen={isOpen}
      caption="Select Link Distribution"
      overlayClassName="SelectLinkDistributionModal__overlay"
      onRequestClose={close}
      modalClassName="SelectLinkDistributionModal__modal">
      <div className="SelectLinkDistributionModal">
        <div className="SelectLinkDistributionModal__filters">
          <label>
            <span>Type</span>
            <input
              value={draftTypeFilter}
              onChange={event => setDraftTypeFilter(event.target.value)}
              placeholder="Type"
            />
          </label>
          <label>
            <span>Tag</span>
            <select
              className="SelectLinkDistributionModal__tag-filter"
              value={tagFilter}
              onChange={event => {
                setTagFilter(event.target.value);
                setPage(1);
              }}>
              <option value="" disabled hidden>
                Tag
              </option>
              {tags.map(tag => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </label>
          <DefaultButton type="button" textId="justSearch" onClick={applySearch} />
          <DefaultButton type="button" textId="justReset" onClick={clearFilters} />
        </div>

        <div className="SelectLinkDistributionModal__table-wrapper">
          <table>
            <thead>
              <tr>
                <th>
                  <CheckBox
                    checked={allVisibleSelected}
                    disabled={!selectableVisibleItems.length}
                    onClick={toggleAllVisible}
                  />
                </th>
                <th>No.</th>
                <th>Type</th>
                <th>Attribute</th>
                <th>FX</th>
                <th>METALS</th>
                <th>COMMODITIES</th>
                <th>Tag</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="8" className="SelectLinkDistributionModal__empty">
                    Loading...
                  </td>
                </tr>
              )}
              {!isLoading && !visibleItems.length && (
                <tr>
                  <td colSpan="8" className="SelectLinkDistributionModal__empty">
                    No link distributions found.
                  </td>
                </tr>
              )}
              {visibleItems.map((item, index) => (
                <tr className={item.isUnavailable ? 'SelectLinkDistributionModal__row--unavailable' : ''} key={item.id}>
                  <td>
                    <CheckBox
                      checked={selectedCodes.includes(item.code)}
                      disabled={item.isUnavailable}
                      onClick={() => toggleSelected(item)}
                    />
                  </td>
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>{item.ibType || '---'}</td>
                  <td>
                    <span
                      className={`SelectLinkDistributionModal__attribute-badge ${
                        item.attribute === 'mark-up' ? 'SelectLinkDistributionModal__attribute-badge--markup' : ''
                      }`}>
                      {item.attribute === 'mark-up' ? 'Mark-up' : item.attribute || 'Basic'}
                    </span>
                  </td>
                  <td>{item.fxSetting ?? '---'}</td>
                  <td>{item.metalsSetting ?? '---'}</td>
                  <td>{item.commoditiesSetting ?? '---'}</td>
                  <td>
                    <span className="SelectLinkDistributionModal__tag-badge">{item.tag || '---'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="SelectLinkDistributionModal__pagination">
          <span>Total {total}</span>
          <select
            aria-label="Page size"
            value={pageSize}
            onChange={event => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}>
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}/page
              </option>
            ))}
          </select>
          <button type="button" disabled={page <= 1} onClick={() => setPage(current => current - 1)}>
            ‹
          </button>
          <strong>{page}</strong>
          <button type="button" disabled={page >= pageCount} onClick={() => setPage(current => current + 1)}>
            ›
          </button>
        </div>

        <div className="SelectLinkDistributionModal__footer">
          <span>
            Selected: <strong>{selectedCodes.length}</strong>
          </span>
          <div>
            <DefaultButton type="button" textId="justCancel" onClick={close} disabled={isSubmitting} />
            <DefaultButton
              type="button"
              textId="justConfirm"
              filled
              onClick={confirm}
              disabled={!selectedCodes.length || isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
      <style jsx global>
        {staticStyles}
      </style>
    </BasicModal>
  );
};

SelectLinkDistributionModal.propTypes = {
  isOpen: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  showFormSuccessMessage: PropTypes.func.isRequired,
  showFormFailMessage: PropTypes.func.isRequired,
};

SelectLinkDistributionModal.defaultProps = {
  isOpen: false,
};

export { SelectLinkDistributionModal };
export default FormMessageHOC(SelectLinkDistributionModal);
